import {
  add,
  constraintDiagnostic,
  minMicro,
  multiply,
  portion,
  subtract,
  ZERO,
} from "./economy-math.js";
import { buildingRule, EMERGENCY_WORKSHOP, outputRule } from "./economy-rules.js";
import type { EconomyDiagnostics, EconomyInput, PowerDepartment } from "./economy-types.js";
import { formatMicro, type Micro, microFromString } from "./exact-numbers.js";
import { effectMultiplier } from "./research-effects.js";

export interface PowerResult {
  readonly operation: Readonly<Record<PowerDepartment, readonly [Micro, Micro]>>;
  readonly diagnostics: EconomyDiagnostics["power"];
  readonly available: Micro;
  readonly unused: Micro;
}

export function settlePower(input: EconomyInput): PowerResult {
  const b = input.buildings;
  const factoryWorksActive =
    input.manufacturing.factoryWorksOperating &&
    Object.values(input.factoryWorksSplit).reduce((sum, value) => sum + value, 0) > 0
      ? b["factory-works"]
      : ZERO;
  const constructionActive =
    (input.manufacturing.newConstruction &&
      input.manufacturing.constructionSplit.newConstruction > 0) ||
    (input.manufacturing.restoration && input.manufacturing.constructionSplit.restoration > 0)
      ? b["construction-yard"]
      : ZERO;
  const assemblyActive = input.manufacturing.assembly ? b["robot-factory"] : ZERO;
  const base = {
    industry: add(
      draw(b["mining-rig"], "mining-rig"),
      draw(constructionActive, "construction-yard"),
      draw(b["rig-factory"], "rig-factory"),
      draw(b["yard-factory"], "yard-factory"),
      draw(factoryWorksActive, "factory-works"),
    ),
    defenses: add(
      draw(b["shield-generator"], "shield-generator"),
      draw(b["supply-depot"], "supply-depot"),
    ),
    "military-production": draw(assemblyActive, "robot-factory"),
    research: draw(b["research-lab"], "research-lab"),
  } satisfies Record<PowerDepartment, Micro>;
  const requested = Object.fromEntries(
    Object.entries(base).map(([id, value]) => [
      id,
      portion(value, BigInt(input.powerSliders[id as PowerDepartment]), 100n),
    ]),
  ) as Record<PowerDepartment, Micro>;
  const available = add(
    whole(EMERGENCY_WORKSHOP.outputs.power),
    multiply(
      b["power-generator"],
      multiplyRate(
        outputRule("power-generator", "power").perSecond,
        effectMultiplier(input.researchEffects, "power-generator-output"),
      ),
    ),
  );
  let remaining = available;
  const received = { industry: ZERO, defenses: ZERO, "military-production": ZERO, research: ZERO };
  for (const id of input.powerPriority) {
    received[id] = minMicro(remaining, requested[id]);
    remaining = subtract(remaining, received[id]);
  }
  const operation = Object.fromEntries(
    Object.entries(base).map(([id, value]) => [
      id,
      [received[id as PowerDepartment], value] as const,
    ]),
  ) as PowerResult["operation"];
  const diagnostics = Object.fromEntries(
    input.powerPriority.map((id) => [
      id,
      constraintDiagnostic("Power", requested[id], received[id]),
    ]),
  ) as EconomyDiagnostics["power"];
  return { operation, diagnostics, available, unused: remaining };
}

function multiplyRate(left: string, right: string): string {
  return formatMicro(multiply(microFromString(left), right));
}

function draw(capacity: Micro, id: Parameters<typeof buildingRule>[0]): Micro {
  return multiply(capacity, String(buildingRule(id).power));
}

function whole(value: number): Micro {
  return (BigInt(value) * 1_000_000n) as Micro;
}
